import { useState, useEffect, useCallback, useRef } from 'react';

const useHistoryManagement = ({ 
  getBacktestHistory, 
  isAuthenticated, 
  user,
  authLoading 
}) => {
  const [backtestHistory, setBacktestHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // CIRCUIT BREAKERS
  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastFetchTimestamp = useRef(0);

  const fetchHistory = useCallback(async (force = false) => {
    // 1. Guard: Auth check
    if (!isAuthenticated || !user?.uid || authLoading) return;

    // 2. Guard: Prevent rapid-fire loops (Throttle to 2 seconds)
    const now = Date.now();
    if (!force && (isFetchingRef.current || (now - lastFetchTimestamp.current < 2000))) {
      console.log('[History] 🛡️ Loop Blocked: Fetch already in progress or throttled.');
      return;
    }

    console.log(`[History] 🛰️ Initiating fetch. Reason: ${force ? 'Manual/Force' : 'Initial/Auto'}`);
    
    isFetchingRef.current = true;
    lastFetchTimestamp.current = now;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    setHistoryLoading(true);
    try {
      const response = await getBacktestHistory();
      const dataToSet = Array.isArray(response) ? response : response?.runs;
      
      if (dataToSet) {
        setBacktestHistory(dataToSet);
        setInitialLoadComplete(true);
        console.log(`[History] ✅ Data synced. Count: ${dataToSet.length}`);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[History] ❌ Fetch failed:', error);
        setInitialLoadComplete(true);
      }
    } finally {
      if (abortControllerRef.current === controller) {
        setHistoryLoading(false);
        isFetchingRef.current = false;
        abortControllerRef.current = null;
      }
    }
  }, [getBacktestHistory, isAuthenticated, user?.uid, authLoading]);

  // Clean, stable refresh function
  const refreshHistory = useCallback(() => {
    return fetchHistory(true); // Force bypasses the throttle
  }, [fetchHistory]);

  // 3. Initial Load & Auth-Change Effect
  // We ONLY trigger this when Uid changes or initial auth completes
  useEffect(() => {
    if (isAuthenticated && user?.uid && !authLoading && !initialLoadComplete) {
      fetchHistory();
    }
  }, [isAuthenticated, user?.uid, authLoading, initialLoadComplete, fetchHistory]);

  return {
    backtestHistory,
    historyLoading,
    initialLoadComplete,
    fetchHistory,
    refreshHistory,
    setBacktestHistory,
  };
};

export default useHistoryManagement;